import { ChangeEvent, FC, useState } from "react";
import { CustomSearch } from "../../../../components/CustomSearch/CustomSearch";
import { useTranslation } from "react-i18next";
import { CustomButton } from "../../../../components/CustomButton/CustomButton";
import useStyles from "./styles";
import { CustomTextInput } from "../../../../components/CustomTextInput/CustomTextInput";
import { CustomTextDatePicker } from "../../../../components/CustomTextDatePicker/CustomTextDatePicker";
import {
  useAssignVehicleToEmployee,
  useGetAllVehiculesFleet,
} from "../../../../api/vehicule";
import { useGetContratByVehicleId } from "../../../../api/contrat";
import { ModalUpdateVehicleContrat } from "../../../Vehicule/UpdateVehicule/VehiculeSection/ModalUpdateVehicleContrat";
import { getCurrentFleetId } from "../../../../utils/getCurrentFleet";
import { colors } from "../../../../styles/colors";
import toast from "react-hot-toast";
import dayjs from "dayjs";

/**
 * Vehicle handed over by the page that opened the wizard, so the user does not
 * have to find it again. Mirrors the claim wizard's PresetVehicle.
 */
type PresetVehicle = {
  vehicleId: number;
  brand?: string;
  brandModel?: string;
  immatriculation?: string;
};

type Props = {
  currentEmployee: TEmployeeWithoutVehicle | null;
  onClickSend?: () => void;
  /**
   * Return to the collaborator file.
   *
   * This step used to render a single "Assign vehicle" button, and the only
   * route forward was a SUCCESSFUL assignment. Every other step in the wizard
   * offers Previous, and step 3 (cards and badges) can be passed through with
   * "Save and next", but this one could not be left at all: a collaborator
   * whose vehicle has no rent contract, or whose fleet has no free vehicle,
   * could neither advance, go back, nor skip. The only escape was abandoning
   * the wizard.
   */
  onClickPrevious?: () => void;
  /**
   * Continue without assigning a vehicle.
   *
   * Not every collaborator gets a car, and the vehicle wizard's mirror step
   * (StepAssignmentOfTheCollaborator) has always offered "Skip this step" for
   * exactly this reason. The same action was optional when started from the
   * vehicle and mandatory when started from the collaborator.
   */
  onClickSkipThisStep?: () => void;
  /**
   * Preselected vehicle from the vehicle sheet's COLLABORATEUR "+" button.
   * When present the search box is replaced by a read-only field: the user
   * already told us which vehicle they meant, and this step's search only
   * lists FREE vehicles, so re-picking could be impossible for a vehicle in
   * maintenance.
   */
  presetVehicle?: PresetVehicle;
};

export const StepAffectation: FC<Props> = ({
  currentEmployee,
  onClickSend,
  onClickPrevious,
  onClickSkipThisStep,
  presetVehicle,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { vehicules } = useGetAllVehiculesFleet(
    getCurrentFleetId(),
    1,
    5000,
    "",
    "F"
  );

  const [selectedVehicleId, setSelectedVehicleId] = useState<number | null>(
    presetVehicle?.vehicleId ?? null
  );
  const [selectedStartDate, setSelectedStartDate] = useState<Date | null>(null);
  const [selectedEndDate, setSelectedEndDate] = useState<Date | null>(null);
  const [openContractModal, setOpenContractModal] = useState(false);

  const presetVehicleLabel = presetVehicle
    ? `${presetVehicle.brand ?? ""} ${presetVehicle.brandModel ?? ""}`.trim() +
      (presetVehicle.immatriculation
        ? ` - ${presetVehicle.immatriculation}`
        : "")
    : "";

  /**
   * The backend refuses to assign a vehicle that has no rent contract
   * ("No contract attach to the vehicle", HTTP 400). That rejection only
   * arrived after the user had filled both dates and pressed Assign, and it
   * pointed at a fix - adding a contract - that lives on a different page, so
   * the wizard had to be abandoned to get anywhere.
   *
   * Check up front instead and offer the contract form inline. The query is
   * skipped while no vehicle is chosen (the hook is disabled on a falsy id).
   */
  const { contrats, isLoading: isLoadingContract } = useGetContratByVehicleId(
    getCurrentFleetId(),
    selectedVehicleId ?? 0
  );
  const isMissingContract =
    selectedVehicleId !== null && !isLoadingContract && contrats.length === 0;

  const { mutate: assign, isPending } = useAssignVehicleToEmployee(
    getCurrentFleetId(),
    currentEmployee?.user_id || 0,
    selectedVehicleId || 0,
    onClickSend
  );

  const onChangeTextFieldVehicle = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (e.target.value.trim().length === 0) {
      setSelectedVehicleId(null);
    }
  };

  const onChangeAutoCompletVehicle = (_: any, vehicle: any) => {
    // Null-guard: CustomSearch sets disableClearable today so this cannot be
    // reached, but the callback is one shared prop change away from receiving
    // null on clear.
    setSelectedVehicleId(vehicle?.vehicleId ?? null);
  };

  const onClickSave = () => {
    if (currentEmployee === null) {
      toast.error("La séléction du collaborateur est obligatoire");
      return;
    } else if (
      selectedVehicleId === null ||
      selectedStartDate === null ||
      selectedEndDate === null
    ) {
      toast.error(t("required.checkFields", "All fields are required"));
    } else if (isMissingContract) {
      // Same condition the backend would reject on, caught before the round
      // trip so the message can point at the button that fixes it.
      toast.error(
        t(
          "vehicule.No contract attach to the vehicle",
          "This vehicle has no rental contract yet. Add one before assigning it."
        )
      );
    } else {
      assign({
        start_date: dayjs(selectedStartDate).format("YYYY-MM-DD"),
        end_date: dayjs(selectedEndDate).format("YYYY-MM-DD"),
      });
    }
  };

  return (
    <div className={classes.container}>
      <div className={classes.containerSearch}>
        {presetVehicle?.vehicleId ? (
          <CustomTextInput
            label={`${t("collaborator.findAvehicle", "Find vehicle")} *`}
            value={presetVehicleLabel}
            disabled
          />
        ) : (
          <CustomSearch
            options={vehicules}
            getOptionLabel={(option: any) =>
              `${option?.brand} ${option?.brandModel} - ${option?.immatriculation}`
            }
            label={`${t("collaborator.findAvehicle", "Find vehicle")} *`}
            placeholder={t("common.find", "Find")}
            marginBottom={"0px"}
            onChange={onChangeAutoCompletVehicle}
            onChangeTextField={onChangeTextFieldVehicle}
          />
        )}
        {isMissingContract && (
          <div style={{ marginBottom: 8 }}>
            <div style={{ color: colors.tertiary, fontSize: 13 }}>
              {t(
                "vehicule.No contract attach to the vehicle",
                "This vehicle has no rental contract yet. Add one before assigning it."
              )}
            </div>
            <CustomButton
              onClick={() => setOpenContractModal(true)}
              label={t("vehicule.addContrat", "Add a rent contract")}
              variant={"secondary"}
              width={"fit-content"}
              height={"40px"}
            />
          </div>
        )}
        <CustomTextDatePicker
          label={`${t(
            "collaborator.assignmentStartDate",
            "Assignment start date"
          )} *`}
          value={selectedStartDate}
          onChange={(value) => setSelectedStartDate(value)}
        />
        <CustomTextDatePicker
          label={`${t(
            "collaborator.assignmentEndDate",
            "Assignment end date"
          )} *`}
          value={selectedEndDate}
          onChange={(value) => setSelectedEndDate(value)}
        />
        <div className={classes.containerButton}>
          <CustomButton
            onClick={onClickPrevious}
            label={t("common.previous", "Previous")}
            variant={"secondary"}
            width={"fit-content"}
            height={"40px"}
          />
          <CustomButton
            onClick={onClickSkipThisStep}
            label={t("vehicule.skipThisStep", "Skip this step")}
            variant={"default"}
            width={"fit-content"}
            height={"40px"}
          />
          <CustomButton
            loading={isPending}
            disabled={isMissingContract}
            onClick={onClickSave}
            label={t("collaborator.assignVehicle", "Assign vehicle")}
            variant={isMissingContract ? "disabled" : "primary"}
            width={"fit-content"}
            height={"40px"}
          />
        </div>
      </div>
      {/* Rendered only while open so the contract query inside it does not run
          for a vehicle the user has not chosen yet. */}
      {openContractModal && selectedVehicleId !== null && (
        <ModalUpdateVehicleContrat
          opeModal={openContractModal}
          vehicleId={selectedVehicleId}
          onCancel={() => setOpenContractModal(false)}
          onClose={() => setOpenContractModal(false)}
        />
      )}
    </div>
  );
};
